// Privacy & Terms remain in English regardless of UI language (legal documents).
// Help, Contact, Medical Sources, and Health Disclaimer have AR and EN variants.

const PRIVACY_SECTIONS = [
    {
        heading: "1. Introduction",
        body:
            "Welcome to 606 FITNESS (\"we\", \"our\", or \"us\"). We respect your privacy and are committed to protecting your personal data. This Privacy Policy explains how we collect, use, store, and share information when you use the 606 FITNESS mobile application (the \"App\") and any related services.",
    },
    {
        heading: "2. Information We Collect",
        body:
            "2.1 Account Information — When you create an account, we collect: your name, email address, phone number, date of birth, gender, height, weight, and fitness goals.\n\n2.2 Profile & Activity — We collect profile photos you upload, your workout subscriptions, programs you create or follow, ratings, and chat history with the AI Coach.\n\n2.3 Device Permissions — With your explicit permission, we may access your device camera (to scan QR codes, take profile photos, and use the AI food scanner), photo library (to pick profile photos), microphone (where applicable), and approximate location (for store delivery, if enabled). You may grant or revoke these permissions at any time via your device settings.\n\n2.4 AI Food Scanner Data — When you use the AI food scanner, the photo you capture is sent securely to Google Gemini for nutritional analysis and is not stored on our servers after the analysis is complete.\n\n2.5 Technical Data — We automatically collect device type, OS version, app version, language preference, and anonymized usage analytics to improve service quality.",
    },
    {
        heading: "3. How We Use Your Information",
        body:
            "• To create and maintain your account and authenticate logins.\n• To deliver fitness and nutrition programs personalized to you.\n• To process subscriptions, payments, and orders within the in-app store.\n• To send email or SMS verification codes (one-time passwords) via SendGrid and Twilio.\n• To generate AI Coach replies and food analysis using Google Gemini models, only with the messages or images you choose to send.\n• To detect fraud, abuse, and violations of our Terms of Service.",
    },
    {
        heading: "4. Third-Party Services",
        body:
            "We use the following sub-processors to operate the App. Each has its own privacy policy:\n\n• SendGrid (Twilio Inc.) — transactional email delivery.\n• Twilio Verify — phone number verification.\n• Google Cloud (Gemini) — AI Coach responses and food image analysis.\n• Stripe — payment processing (when enabled).\n• MongoDB Atlas — encrypted database storage.",
    },
    {
        heading: "5. Data Storage & Security",
        body:
            "Your data is stored on secured servers protected by industry-standard encryption (HTTPS in transit, encryption at rest). Passwords are hashed using bcrypt and never stored in plain text. We retain your data for as long as your account is active.",
    },
    {
        heading: "6. Your Rights",
        body:
            "• Access — request a copy of the data we hold about you.\n• Correction — update inaccurate information directly in the App or by contacting us.\n• Deletion — request permanent deletion of your account and all associated data, either through Settings > Account > Delete Account inside the App, or by emailing privacy@606fitness.com. Deletion is completed within 30 days.\n• Portability — request an export of your data in JSON format.\n• Withdrawal of consent — at any time, with effect for future processing only.\n\nTo exercise these rights, email us at privacy@606fitness.com.",
    },
    {
        heading: "7. Health Disclaimer",
        body:
            "606 FITNESS provides fitness, nutrition, and lifestyle information for educational and motivational purposes only. The App does not replace professional medical advice, diagnosis, or treatment. Always consult a qualified healthcare provider before starting any exercise program, changing your diet, or using dietary supplements. See our full Health & Safety Disclaimer and Medical Sources pages for more information.",
    },
    {
        heading: "8. Children",
        body:
            "606 FITNESS is intended for users aged 13 and older. We do not knowingly collect personal data from children under 13. If we discover that we have collected data from a child under 13, we will delete it promptly.",
    },
    {
        heading: "9. International Transfers",
        body:
            "Our servers are located in regions chosen for performance and reliability. By using the App, you consent to the transfer of your information across borders, in accordance with applicable data-protection laws.",
    },
    {
        heading: "10. Changes to this Policy",
        body:
            "We may update this Privacy Policy from time to time. Material changes will be notified in-app or via email. The \"Last updated\" date at the top reflects the latest revision.",
    },
    {
        heading: "11. Contact Us",
        body:
            "606 FITNESS\nEmail: privacy@606fitness.com\nSupport: support@606fitness.com",
    },
];

const TERMS_SECTIONS = [
    {
        heading: "1. Acceptance of Terms",
        body:
            "By downloading, accessing, or using the 606 FITNESS application (the \"App\"), you agree to be bound by these Terms of Service (\"Terms\"). If you do not agree, please do not use the App.",
    },
    {
        heading: "2. Eligibility",
        body:
            "You must be at least 13 years old to use 606 FITNESS, and at least 17 years old to acknowledge the Health & Safety Disclaimer and assume personal responsibility for using fitness and nutrition guidance. By creating an account you represent and warrant that you meet these age requirements and have the legal capacity to enter into these Terms.",
    },
    {
        heading: "3. Health & Safety Disclaimer",
        body:
            "606 FITNESS provides fitness, nutrition, and lifestyle content for informational and motivational purposes only. The App is not a substitute for professional medical advice, diagnosis, or treatment. Always consult a qualified health professional before beginning any exercise program, changing your diet, or taking dietary supplements. You assume all risks of injury arising from your use of the App and any of its content. See our Health & Safety Disclaimer for full details.",
    },
    {
        heading: "4. Accounts",
        body:
            "• You are responsible for keeping your login credentials confidential.\n• You are responsible for all activity that occurs under your account.\n• Notify us immediately of any unauthorized use of your account.",
    },
    {
        heading: "5. User-Generated Content",
        body:
            "Trainers may create and publish programs, exercises, meals, and accompanying media inside the App. By submitting content you grant 606 FITNESS a worldwide, non-exclusive, royalty-free license to host, display, and distribute that content within the App. You are solely responsible for ensuring that you own or have the right to share any content you upload.\n\nYou agree not to upload content that is unlawful, defamatory, infringing, sexually explicit, hateful, or otherwise objectionable. 606 FITNESS reserves the right to remove any content that violates these Terms.",
    },
    {
        heading: "6. Subscriptions & Purchases",
        body:
            "Some programs and products may require payment. All transactions are processed by Stripe or another regulated payment provider. Prices are listed in AED unless otherwise indicated. Subscriptions automatically renew unless canceled before the renewal date.\n\nRefunds, where applicable, follow Apple App Store / Google Play store policy. Contact support@606fitness.com for assistance.",
    },
    {
        heading: "7. Store Region",
        body:
            "The in-app store for physical goods (equipment, sportswear, shoes) is currently available only in the United Arab Emirates. Availability is subject to change.",
    },
    {
        heading: "8. Prohibited Conduct",
        body:
            "• Reverse engineering, decompiling, or interfering with the App.\n• Using the App to impersonate another person or misrepresent your affiliation.\n• Spamming, scraping, or otherwise abusing the platform.\n• Attempting to gain unauthorized access to other users' accounts or to our infrastructure.",
    },
    {
        heading: "9. Intellectual Property",
        body:
            "All trademarks, logos, software, designs, and content provided by 606 FITNESS remain the exclusive property of 606 FITNESS. Nothing in these Terms grants you any right or license to use them outside of the App without prior written consent.",
    },
    {
        heading: "10. Termination",
        body:
            "We may suspend or terminate your access to the App at any time, with or without notice, if you violate these Terms or engage in conduct that we believe harms other users or the platform. You may delete your account at any time via the in-app Settings or by emailing privacy@606fitness.com.",
    },
    {
        heading: "11. Limitation of Liability",
        body:
            "To the maximum extent permitted by law, 606 FITNESS and its operators shall not be liable for any indirect, incidental, special, or consequential damages arising out of or related to your use of the App.",
    },
    {
        heading: "12. Changes to these Terms",
        body:
            "We may update these Terms from time to time. Continued use of the App after changes are posted constitutes acceptance of the revised Terms.",
    },
    {
        heading: "13. Governing Law",
        body:
            "These Terms are governed by the laws of the United Arab Emirates, without regard to conflict of laws principles. Any dispute shall be resolved exclusively in the competent courts of the UAE.",
    },
    {
        heading: "14. Contact",
        body:
            "606 FITNESS\nEmail: support@606fitness.com\nLegal: legal@606fitness.com",
    },
];

// Medical Sources — bilingual (each item kept in EN since source URLs are international)
const MEDICAL_SOURCES_BLOCKS = [
    {
        groupAr: "1. مؤشر كتلة الجسم (BMI)",
        groupEn: "1. Body Mass Index (BMI)",
        items: [
            {
                label: "World Health Organization (WHO) — BMI classification",
                url: "https://www.who.int/news-room/fact-sheets/detail/obesity-and-overweight",
            },
            {
                label: "Centers for Disease Control (CDC) — About Adult BMI",
                url: "https://www.cdc.gov/healthyweight/assessing/bmi/adult_bmi/index.html",
            },
        ],
    },
    {
        groupAr: "2. حساب السعرات (BMR / TDEE)",
        groupEn: "2. Calorie Calculation (BMR / TDEE)",
        items: [
            {
                label:
                    "Mifflin-St Jeor Equation — Journal of the Academy of Nutrition and Dietetics",
                url: "https://pubmed.ncbi.nlm.nih.gov/2305711/",
            },
            {
                label: "Institute of Medicine — Dietary Reference Intakes",
                url: "https://nap.nationalacademies.org/catalog/10490/dietary-reference-intakes-for-energy-carbohydrate-fiber-fat-fatty-acids-cholesterol-protein-and-amino-acids",
            },
        ],
    },
    {
        groupAr: "3. التوزيع الغذائي (Macros)",
        groupEn: "3. Macronutrient Distribution (Macros)",
        items: [
            {
                label: "USDA Dietary Guidelines for Americans 2020–2025",
                url: "https://www.dietaryguidelines.gov/",
            },
            {
                label: "Institute of Medicine — Macronutrients AMDRs",
                url: "https://www.nal.usda.gov/sites/default/files/fnic_uploads/macronutrients.pdf",
            },
        ],
    },
    {
        groupAr: "4. بيانات الأطعمة",
        groupEn: "4. Food & Nutrition Data",
        items: [
            {
                label: "USDA FoodData Central",
                url: "https://fdc.nal.usda.gov/",
            },
            {
                label: "U.S. Food and Drug Administration (FDA) — Nutrition Facts Label",
                url: "https://www.fda.gov/food/nutrition-education-resources-materials/new-nutrition-facts-label",
            },
        ],
    },
    {
        groupAr: "5. التمارين، الماء والنوم",
        groupEn: "5. Exercise, Hydration & Sleep",
        items: [
            {
                label: "WHO — Physical Activity Guidelines",
                url: "https://www.who.int/news-room/fact-sheets/detail/physical-activity",
            },
            {
                label: "American Heart Association (AHA) — Recommendations for Physical Activity",
                url: "https://www.heart.org/en/healthy-living/fitness/fitness-basics/aha-recs-for-physical-activity-in-adults",
            },
            {
                label: "Institute of Medicine — Water, Potassium, Sodium Intake",
                url: "https://nap.nationalacademies.org/catalog/10925/dietary-reference-intakes-for-water-potassium-sodium-chloride-and-sulfate",
            },
            {
                label: "CDC — Sleep & Sleep Disorders",
                url: "https://www.cdc.gov/sleep/index.html",
            },
        ],
    },
];

const makeMedicalSourcesSections = (lang) =>
    MEDICAL_SOURCES_BLOCKS.map((block) => ({
        heading: lang === "ar" ? block.groupAr : block.groupEn,
        body: block.items.map((it) => `• ${it.label}\n${it.url}`).join("\n\n"),
    }));

const HEALTH_DISCLAIMER = {
    ar: {
        title: "تنبيه الصحة والسلامة",
        subtitle: "Health & Safety Disclaimer",
        lastUpdated: "June 2025",
        sections: [
            {
                heading: "⚠️ تنبيه مهم قبل الاستخدام",
                body:
                    "606 FITNESS تطبيق للّياقة البدنية والتغذية، يقدّم معلومات لأغراض تعليمية وتحفيزية فقط ولا يعتبر بأي حال بديلاً عن الاستشارة الطبية المتخصصة أو التشخيص أو العلاج.",
            },
            {
                heading: "1. استشر طبيبك أولاً",
                body:
                    "قبل البدء بأي برنامج تمارين أو نظام غذائي أو تناول مكمّلات، استشر طبيباً مختصاً، خصوصاً إذا كنت تعاني من حالة طبية مزمنة (قلب، سكري، ضغط، حمل، إصابة سابقة...إلخ).",
            },
            {
                heading: "2. تحمّل المسؤولية الشخصية",
                body:
                    "أنت تتحمّل كامل المسؤولية عن أي إصابة أو ضرر قد ينتج من استخدامك للتطبيق. توقّف فوراً عن أي تمرين إذا شعرت بألم، دوار، ضيق تنفّس، أو أي عرض غير طبيعي، واطلب المساعدة الطبية إذا لزم الأمر.",
            },
            {
                heading: "3. شرح أداء التمارين",
                body:
                    "اتّبع الفيديوهات والإرشادات بدقّة، وابدأ بالأوزان الخفيفة والإحماء قبل أي تمرين. لا تتجاوز قدراتك ولا تكرّر الحركة بطريقة خاطئة.",
            },
            {
                heading: "4. مصادر البيانات الصحية",
                body:
                    "كل المعادلات والقيم الغذائية المستخدمة في التطبيق (BMI، السعرات، الماكروز...إلخ) مصدرها مؤسسات صحية معتمدة عالمياً. يمكنك الاطلاع على القائمة الكاملة في صفحة المصادر الطبية.",
            },
            {
                heading: "إقرار المستخدم",
                body:
                    "باستمرارك في استخدام التطبيق فإنك تقرّ بأنّك:\n\n☑️ تبلغ من العمر 17 سنة فأكثر.\n☑️ قرأت وفهمت هذا التنبيه وتقبل المسؤولية الكاملة عن استخدامك للتطبيق.",
            },
        ],
    },
    en: {
        title: "Health & Safety Disclaimer",
        subtitle: "606 FITNESS",
        lastUpdated: "June 2025",
        sections: [
            {
                heading: "⚠️ Important Notice Before Use",
                body:
                    "606 FITNESS is a fitness and nutrition application that provides information for educational and motivational purposes only and is not, under any circumstance, a substitute for professional medical advice, diagnosis, or treatment.",
            },
            {
                heading: "1. Consult Your Doctor First",
                body:
                    "Before starting any exercise program, dietary plan, or taking supplements, consult a qualified healthcare provider — especially if you have a chronic medical condition (heart disease, diabetes, hypertension, pregnancy, prior injury, etc.).",
            },
            {
                heading: "2. Personal Responsibility",
                body:
                    "You assume full responsibility for any injury or harm that may arise from your use of the App. Stop immediately if you feel pain, dizziness, shortness of breath, or any abnormal symptom, and seek medical assistance if needed.",
            },
            {
                heading: "3. Exercise Execution",
                body:
                    "Follow the videos and instructions precisely. Begin with light weights and a proper warm-up before any workout. Do not exceed your capacity or repeat movements with improper form.",
            },
            {
                heading: "4. Health Data Sources",
                body:
                    "All formulas and nutritional values used in the App (BMI, calories, macros, etc.) are derived from internationally recognized health institutions. See the Medical Sources page for the full list.",
            },
            {
                heading: "User Acknowledgement",
                body:
                    "By continuing to use the App, you acknowledge that:\n\n☑️ You are 17 years of age or older.\n☑️ You have read and understood this disclaimer and accept full responsibility for your use of the App.",
            },
        ],
    },
};

const SUPPORT_BY_LANG = {
    ar: {
        help: {
            title: "الدعم والمساعدة",
            subtitle: "Help & Support",
            lastUpdated: "June 2025",
            sections: [
                {
                    heading: "كيف أبدأ؟",
                    body:
                        "حمّل التطبيق من متجر التطبيقات، أنشئ حسابك، حدّد هدفك (بناء عضل، حرق دهون، أو لياقة عامة)، وسيبدأ الذكاء الاصطناعي بتجهيز خطتك تلقائياً.",
                },
                {
                    heading: "نسيت كلمة المرور",
                    body:
                        "اضغط على \"نسيت كلمة المرور\" في شاشة تسجيل الدخول، وأدخل بريدك الإلكتروني أو رقم جوالك. سنرسل لك رمز تحقق فوري لإعادة التعيين.",
                },
                {
                    heading: "الاشتراكات والمدفوعات",
                    body:
                        "تتم جميع المعاملات بأمان عبر Stripe أو متجر التطبيقات. للاطلاع على اشتراكاتك أو إلغائها، اذهب إلى الإعدادات > الاشتراكات داخل التطبيق.",
                },
                {
                    heading: "متجر المعدات الرياضية",
                    body:
                        "متجر المنتجات الفعلية (معدات، ملابس، أحذية) متوفر حالياً داخل دولة الإمارات العربية المتحدة فقط، مع شحن سريع وضمان جودة.",
                },
                {
                    heading: "مدرّب الذكاء الاصطناعي (AI Coach)",
                    body:
                        "تحدّث مع المدرّب الذكي 24/7. ينصحك بالتمارين، يضبط خطّتك الغذائية، ويجيب على أسئلتك في اللياقة والتغذية بشكل فوري.",
                },
                {
                    heading: "حذف الحساب",
                    body:
                        "تقدر تحذف حسابك في أي وقت من الإعدادات > الحساب > حذف الحساب داخل التطبيق، أو من صفحة إدارة الحساب على الويب: https://606fitness.com/account . كل بياناتك تُحذف بشكل دائم خلال 30 يوماً.",
                },
                {
                    heading: "لم تجد إجابتك؟",
                    body:
                        "تواصل معنا مباشرة على support@606fitness.com وفريقنا يرد عليك خلال 24 ساعة.",
                },
            ],
        },
        privacy: {
            title: "سياسة الخصوصية",
            subtitle: "Privacy Policy",
            lastUpdated: "June 2025",
            sections: PRIVACY_SECTIONS,
        },
        terms: {
            title: "شروط الاستخدام",
            subtitle: "Terms of Service",
            lastUpdated: "June 2025",
            sections: TERMS_SECTIONS,
        },
        medicalSources: {
            title: "المصادر الطبية",
            subtitle: "Medical Sources",
            lastUpdated: "June 2025",
            sections: makeMedicalSourcesSections("ar"),
        },
        healthDisclaimer: HEALTH_DISCLAIMER.ar,
        contact: {
            title: "تواصل معنا",
            subtitle: "Contact Us",
            lastUpdated: "",
            sections: [
                {
                    heading: "الدعم العام",
                    body:
                        "لأي سؤال أو استفسار عن التطبيق:\nsupport@606fitness.com\nالرد خلال 24 ساعة.",
                },
                {
                    heading: "الخصوصية والبيانات",
                    body:
                        "لطلبات حذف الحساب أو الاستفسار عن بياناتك:\nprivacy@606fitness.com",
                },
                {
                    heading: "الشؤون القانونية",
                    body: "للقضايا القانونية والشراكات:\nlegal@606fitness.com",
                },
                { heading: "الموقع", body: "الإمارات العربية المتحدة (UAE)" },
            ],
        },
    },

    en: {
        help: {
            title: "Help & Support",
            subtitle: "FAQ",
            lastUpdated: "June 2025",
            sections: [
                {
                    heading: "How do I get started?",
                    body:
                        "Download the app from your store, create an account, set your goal (muscle gain, fat loss, or general fitness), and the AI will prepare your plan automatically.",
                },
                {
                    heading: "I forgot my password",
                    body:
                        "Tap \"Forgot Password\" on the login screen and enter your email or phone number. We'll send you an instant verification code to reset it.",
                },
                {
                    heading: "Subscriptions & Payments",
                    body:
                        "All transactions are processed securely via Stripe or your app store. To view or cancel subscriptions, go to Settings > Subscriptions inside the app.",
                },
                {
                    heading: "Equipment Store",
                    body:
                        "The physical products store (equipment, sportswear, shoes) is currently available only in the United Arab Emirates, with fast shipping and a quality guarantee.",
                },
                {
                    heading: "AI Coach",
                    body:
                        "Chat with your smart coach 24/7. It recommends exercises, tunes your nutrition plan, and answers your fitness questions instantly.",
                },
                {
                    heading: "Deleting your account",
                    body:
                        "You can delete your account anytime from Settings > Account > Delete Account inside the app, or from the web Account page: https://606fitness.com/account . All your data is permanently removed within 30 days.",
                },
                {
                    heading: "Didn't find your answer?",
                    body:
                        "Contact us directly at support@606fitness.com and our team will reply within 24 hours.",
                },
            ],
        },
        privacy: {
            title: "Privacy Policy",
            subtitle: "606 FITNESS",
            lastUpdated: "June 2025",
            sections: PRIVACY_SECTIONS,
        },
        terms: {
            title: "Terms of Service",
            subtitle: "606 FITNESS",
            lastUpdated: "June 2025",
            sections: TERMS_SECTIONS,
        },
        medicalSources: {
            title: "Medical Sources",
            subtitle: "606 FITNESS",
            lastUpdated: "June 2025",
            sections: makeMedicalSourcesSections("en"),
        },
        healthDisclaimer: HEALTH_DISCLAIMER.en,
        contact: {
            title: "Contact Us",
            subtitle: "606 FITNESS",
            lastUpdated: "",
            sections: [
                {
                    heading: "General Support",
                    body:
                        "For any question about the app:\nsupport@606fitness.com\nWe reply within 24 hours.",
                },
                {
                    heading: "Privacy & Data",
                    body:
                        "For account deletion requests or data inquiries:\nprivacy@606fitness.com",
                },
                {
                    heading: "Legal",
                    body: "For legal matters and partnerships:\nlegal@606fitness.com",
                },
                { heading: "Location", body: "United Arab Emirates (UAE)" },
            ],
        },
    },
};

export const getSupport = (lang) =>
    SUPPORT_BY_LANG[lang] || SUPPORT_BY_LANG.ar;
